import {
  Container,
  Stack,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  CardContent,
  Snackbar,
  Alert,
  TextField,
} from '@mui/material';
import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'src/routes/hooks';
import { LoadingScreen } from 'src/components/loading-screen';
import { getUserTelePackageResponse, updateUserTelePackageResponse } from 'src/services/telehealth-packages.service';
import { RHFTextField } from 'src/components/hook-form';
import RHFImageUpload from 'src/components/hook-form/rhf-image-upload';
import { getProduct } from 'src/services/products.service';
import { useAuthContext } from '../../../auth/hooks';

export default function UserTelePackageResponseView() {
  const { user } = useAuthContext();
  const { userResponseId } = useParams();
  const [responseDetails, setResponseDetails] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [productQuantity, setProductQuantity] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');



  const schema = Yup.object().shape({
    doctor_notes: Yup.string().nullable(),
    prescriptions_image: Yup.array().of(Yup.string()).nullable(),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      doctor_notes: '',
      prescriptions_image: [],
    },
  });

  const { setValue, watch, getValues } = methods;
  const prescriptionsImage = watch('prescriptions_image');

  useEffect(() => {
    getUserTelePackageResponse(userResponseId)
      .then((res) => {
        setResponseDetails(res);
        setValue('doctor_notes', res.doctor_notes || '');
        setValue('prescriptions_image', res.prescriptions_image || []);
      })
      .catch(console.log);
  }, [userResponseId, setValue]);

  useEffect(() => {
    if (!responseDetails) return;
    const productIds = [
      ...new Set(responseDetails.telecare_package_id.therapy_ids.flatMap((t) => t.products?.map((p) => p.id) || []))
    ];
    Promise.all(productIds.map((id) => getProduct(id, process.env.REACT_APP_TELE_CLINIC_ID))).then(setProducts).catch(console.log);
  }, [responseDetails]);

  const hasPrescriptionRequiredProduct = () =>
    responseDetails.products_array?.some((pa) => {
      const prod = products.find((p) => p.id === pa.product_id);
      return prod?.prescription_required === true;
    });

  const hasAtLeastOneProduct = () => responseDetails.products_array && responseDetails.products_array.length > 0;

  const handleAddProduct = (product) => {
    if (!productQuantity || productQuantity < 1) {
      setSnackbarMessage('Quantity must be at least 1');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const existsIndex = responseDetails.products_array?.findIndex((p) => p.product_id === product.id);

    if (existsIndex >= 0) {
      // Update quantity if already exists
      const updated = [...responseDetails.products_array];
      updated[existsIndex].quantity = productQuantity;
      setResponseDetails((prev) => ({ ...prev, products_array: updated }));
    } else {
      // Add new product
      setResponseDetails((prev) => ({
        ...prev,
        products_array: [...(prev.products_array || []), { product_id: product.id, quantity: productQuantity }],
      }));
    }

    setProductModalOpen(false);
    setProductQuantity(1);
  };

  const handleStatusUpdate = (status) => {
    if (!getValues('doctor_notes') || getValues('doctor_notes').trim() === '') {
      setSnackbarMessage('Doctor notes are required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!hasAtLeastOneProduct()) {
      setSnackbarMessage('Please add at least one product');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (status === 'approved' && hasPrescriptionRequiredProduct() && (getValues('prescriptions_image') || []).length === 0) {
      setSnackbarMessage('Prescription image required for selected products');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    updateUserTelePackageResponse(userResponseId, {
      doctor_notes: getValues('doctor_notes'),
      prescriptions_image: getValues('prescriptions_image'),
      status,
      products_array: responseDetails.products_array,
      doctor_id: user.doctor_id,
    })
      .then((res) => {
        setResponseDetails(res);
        setSnackbarMessage(`Response ${status} successfully`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      })
      .catch((err) => {
        setSnackbarMessage(`Error: ${err.message}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };


  if (!responseDetails) return <LoadingScreen />;

  const disableApprove = responseDetails.status === 'approved';
  const disableReject = responseDetails.status === 'approved' || responseDetails.status === 'rejected';

  return (
    <Container>
      <Typography variant="h4" mb={3}>User Telepackage Response</Typography>

      <FormProvider {...methods}>
        <Stack gap={3}>
          <Stack>
            <Typography variant="h6">User</Typography>
            <Typography>{responseDetails.user_id.username}</Typography>
          </Stack>

          <Stack>
            <Typography variant="h6">Telecare Package</Typography>
            <Typography>{responseDetails.telecare_package_id.package_name}</Typography>
          </Stack>

          <Stack>
            <Typography variant="h6">Status</Typography>
            <Typography>{responseDetails.status}</Typography>
          </Stack>

          <Stack>
            <Typography variant="h6">Consultation Fee</Typography>
            <Typography>{responseDetails.consultation_fee ?? '-'}</Typography>
          </Stack>

          <Stack>
            <Typography variant="h6">Responses</Typography>

            {responseDetails.telecare_package_id.questions_id.questions.map((q) => {
              const answer = responseDetails.content.find(
                (c) => c.question_number === q.questionNumber
              );

              let renderedValue = <Typography>-</Typography>;

              if (q.questionType === 'image-upload') {
                let images = [];

                if (Array.isArray(answer?.response)) {
                  images = answer.response;
                } else if (answer?.response) {
                  images = [answer.response];
                }

                if (images.length) {
                  renderedValue = (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {images.map((url, idx) => (
                        <img
                          key={idx}
                          src={url}
                          alt={`Upload-${idx}`}
                          style={{
                            maxWidth: 450,
                            maxHeight: 450,
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            borderRadius: 8,
                          }}
                        />
                      ))}
                    </Stack>
                  );
                }
              } else if (Array.isArray(answer?.response)) {
                renderedValue = <Typography>{answer.response.join(', ')}</Typography>;
              } else if (answer?.response) {
                renderedValue = <Typography>{answer.response}</Typography>;
              }

              return (
                <Stack key={q.questionNumber} mb={1} spacing={1}>
                  <Typography variant="subtitle2">{q.questionText}</Typography>
                  {renderedValue}
                </Stack>
              );
            })}
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RHFTextField name="doctor_notes" label="Doctor Notes" multiline rows={4} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <RHFImageUpload
                name="prescriptions_image"
                initialImage={prescriptionsImage}
                setValue={setValue}
                fileName={`prescription_${Date.now()}`}
                folderName="images/prescriptions"
                displayImages
                multipleFiles
                maxFiles={10}
                label="Prescription Images"
              />
            </Grid>
          </Grid>

          <Stack>
            <Typography variant="h6">Recommended Products</Typography>
            <Grid container spacing={2}>
              {products.map((p) => {
                const isAdded = responseDetails.products_array?.some(pa => pa.product_id === p.id);
                const quantity = responseDetails.products_array?.find(pa => pa.product_id === p.id)?.quantity || 1;
                return (
                  <Grid item xs={12} md={4} key={p.id}>
                    <Card
                      sx={{ cursor: 'pointer', border: isAdded ? '2px solid green' : '1px solid #e0e0e0', opacity: isAdded ? 0.6 : 1 }}
                      onClick={() => {
                        setSelectedProduct(p);
                        setProductQuantity(quantity);
                        setProductModalOpen(true);
                      }}
                    >
                      <CardMedia component="img" height="140" image={p.image_url} />
                      <CardContent>
                        <Typography fontWeight={600}>{p.product_name}</Typography>
                        <Typography>₹{p.price}</Typography>
                        {p.prescription_required && <Typography color="error" variant="caption">Prescription Required</Typography>}
                        {isAdded && <Typography color="green" variant="subtitle2">Added (Qty: {quantity})</Typography>}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>

          <Stack direction="row" gap={2} justifyContent="flex-end">
            <Button
              variant="contained"
              color="success"
              disabled={disableApprove}
              onClick={() => handleStatusUpdate('approved')}
            >
              Approve
            </Button>

            <Button
              variant="contained"
              color="error"
              disabled={disableReject}
              onClick={() => handleStatusUpdate('rejected')}
            >
              Reject
            </Button>
          </Stack>

        </Stack>
      </FormProvider>

      <Dialog open={productModalOpen} onClose={() => setProductModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedProduct?.product_name}</DialogTitle>
        <DialogContent>
          <img src={selectedProduct?.image_url} alt="" style={{ width: '100%', borderRadius: 8 }} />
          <Typography mt={2}>₹{selectedProduct?.price}</Typography>
          {selectedProduct?.prescription_required && <Typography color="error">Prescription Required</Typography>}
          <TextField
            type="number"
            label="Quantity"
            value={productQuantity}
            onChange={(e) => setProductQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))
            }
            sx={{ mt: 2, width: 120 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => handleAddProduct(selectedProduct)}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>{snackbarMessage}</Alert>
      </Snackbar>
    </Container>
  );
}
